/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CellPosition,
  TableEditableColumnProps,
  TableEditableProps,
} from './table-editable.type'

import { ArrowDownIcon, ArrowUpIcon, SortIcon } from '../../../icons'
import { Skeleton, Text } from '../../core'
import { EditableColumn } from '../editable-column'
import { generateClassesBodyColumn } from './table-editable.constants'
import styles from './table-editable.module.css'

export const TableEditable = <T extends Record<string, any>>({
  columns,
  data,
  isLoading = false,
  className = '',
  onCellEdit,
  sortBy,
  onSortBy,
  limit = 10,
}: TableEditableProps<T>) => {
  const [selectedCell, setSelectedCell] = useState<CellPosition | null>(null)
  const [editingCell, setEditingCell] = useState<CellPosition | null>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  const cellRefs = useRef<Record<string, HTMLDivElement>>({})

  function getValueByPath<U>(
    object: U,
    path: string | number,
  ): string | undefined {
    const keys = `${path}`.split('.')
    let result: any = object
    for (const key of keys) {
      if (result == null) {
        return '-'
      }
      result = result[key]
    }
    return result
  }

  const getCellKey = (row: number, col: number) => `${row}-${col}`

  const moveSelection = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right' | 'tab') => {
      if (!selectedCell || editingCell) return

      let newRow = selectedCell.row
      let newCol = selectedCell.col

      switch (direction) {
        case 'up':
          newRow = Math.max(0, selectedCell.row - 1)
          break
        case 'down':
          newRow = Math.min(data.length - 1, selectedCell.row + 1)
          break
        case 'left':
          newCol = Math.max(0, selectedCell.col - 1)
          break
        case 'right':
        case 'tab':
          newCol = selectedCell.col + 1
          if (newCol >= columns.length) {
            newCol = 0
            newRow = Math.min(data.length - 1, selectedCell.row + 1)
          }
          break
      }

      setSelectedCell({ row: newRow, col: newCol })

      const cellKey = getCellKey(newRow, newCol)
      const cellElement = cellRefs.current[cellKey]
      if (cellElement) {
        cellElement.focus()
      }
    },
    [selectedCell, editingCell, data.length, columns.length],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!selectedCell) return

      if (editingCell) {
        switch (event.key) {
          case 'Escape':
            event.preventDefault()
            setEditingCell(null)
            break
          case 'Tab':
            event.preventDefault()
            setEditingCell(null)
            setTimeout(() => moveSelection('tab'), 50)
            break
        }
        return
      }

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault()
          moveSelection('up')
          break
        case 'ArrowDown':
          event.preventDefault()
          moveSelection('down')
          break
        case 'ArrowLeft':
          event.preventDefault()
          moveSelection('left')
          break
        case 'ArrowRight':
          event.preventDefault()
          moveSelection('right')
          break
        case 'Tab':
          event.preventDefault()
          moveSelection('tab')
          break
        case 'Enter':
          event.preventDefault()
          if (columns[selectedCell.col]?.isEditable !== false) {
            setEditingCell(selectedCell)
          }
          break
      }
    },
    [selectedCell, editingCell, moveSelection, columns],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const handleCellClick = (
    row: number,
    col: number,
    event?: React.MouseEvent,
  ) => {
    if (editingCell?.row === row && editingCell?.col === col) {
      event?.stopPropagation()
      return
    }

    const target = event?.target as Element
    if (target) {
      const isInsideEditableColumn =
        target.closest('[data-editable-column]') ||
        target.closest('.editable-column') ||
        target.closest('input') ||
        target.closest('select') ||
        target.closest('textarea') ||
        target.closest('button')

      if (isInsideEditableColumn && editingCell) {
        event?.stopPropagation()
        return
      }
    }

    setSelectedCell({ row, col })
    setEditingCell(null)
  }

  const handleCellDoubleClick = (
    row: number,
    col: number,
    event?: React.MouseEvent,
  ) => {
    if (editingCell?.row === row && editingCell?.col === col) {
      event?.stopPropagation()
      return
    }

    const target = event?.target as Element
    if (target) {
      const isInsideEditableColumn =
        target.closest('[data-editable-column]') ||
        target.closest('.editable-column') ||
        target.closest('input') ||
        target.closest('select') ||
        target.closest('textarea') ||
        target.closest('button')

      if (isInsideEditableColumn && editingCell) {
        event?.stopPropagation()
        return
      }
    }

    if (columns[col]?.isEditable !== false) {
      setSelectedCell({ row, col })
      setEditingCell({ row, col })
    }
  }

  const handleEditSubmit = async (row: number, col: number, value: any) => {
    const column = columns[col]
    const record = data[row]

    try {
      if (column.onSubmit) {
        await column.onSubmit(record, value, row)
      }
      onCellEdit?.(row, column.key, value)
      setEditingCell(null)
      setTimeout(() => {
        moveSelection('tab')
      }, 50)
    } catch (error) {}
  }

  const renderCellContent = (
    record: T,
    column: TableEditableColumnProps<T>,
    rowIndex: number,
    colIndex: number,
  ) => {
    const value = column?.value
      ? column.value(record)
      : getValueByPath(record, column.dataKey as string)
    const isCellEditing =
      editingCell?.row === rowIndex && editingCell?.col === colIndex

    if (isCellEditing && column.isEditable !== false && column.editableProps) {
      const editableProps =
        column.editableProps && typeof column.editableProps === 'function'
          ? column.editableProps(record)
          : (column.editableProps as any)
      return (
        <EditableColumn
          {...editableProps}
          value={value}
          isEditing={isCellEditing}
          onSubmit={async (newValue) => {
            await handleEditSubmit(rowIndex, colIndex, newValue)
          }}
          onEditingChange={(editing) => {
            if (!editing) {
              setEditingCell(null)
            }
          }}
          render={
            column.render
              ? column.render({ value, record, index: rowIndex })
              : value || '-'
          }
        />
      )
    }

    if (column.render) {
      return <Text>{column.render({ value, record, index: rowIndex })}</Text>
    }

    return <Text>{value || '-'}</Text>
  }

  const SortBy = ({ sortKey }: { sortKey: string }) => {
    const isDesc = sortBy?.[0] === '-'
    const isActive = !!sortBy?.includes(sortKey)

    let index = isActive ? (isDesc ? 2 : 1) : 0

    const sorts = ['', sortKey, `-${sortKey}`]

    const handleOnSortBy = () => {
      index = index === sorts.length - 1 ? 0 : index + 1

      onSortBy?.(sorts[index])
    }

    return (
      <button
        className={generateClassesBodyColumn(isActive)}
        onClick={handleOnSortBy}
      >
        {isActive ? isDesc ? <ArrowDownIcon /> : <ArrowUpIcon /> : <></>}
        {!isActive ? <SortIcon /> : <></>}
      </button>
    )
  }

  return (
    <div className={`${styles['table-editable']} ${className}`} ref={tableRef}>
      <div className={styles['table-wrapper']}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={`header-${column.key}-${index}`}
                  style={{
                    width: column.width,
                    minWidth: column.width,
                    maxWidth: column.width,
                  }}
                  className={styles['header-cell']}
                >
                  <div>
                    <Text
                      variant="title-base"
                      className={styles['header-cell-title']}
                    >
                      {column.title}
                    </Text>
                    {column.sortKey && <SortBy sortKey={column.sortKey} />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!isLoading
              ? data.map((record, rowIndex) => (
                  <tr key={`row-${rowIndex}`}>
                    {columns.map((column, colIndex) => {
                      const cellKey = getCellKey(rowIndex, colIndex)
                      const isSelected =
                        selectedCell?.row === rowIndex &&
                        selectedCell?.col === colIndex
                      const isCellEditing =
                        editingCell?.row === rowIndex &&
                        editingCell?.col === colIndex

                      return (
                        <td
                          key={`cell-${column.key}-${rowIndex}-${colIndex}`}
                          ref={(el) => {
                            if (el) cellRefs.current[cellKey] = el
                          }}
                          tabIndex={isCellEditing ? -1 : 0}
                          style={{
                            width: column.width,
                            minWidth: column.width,
                            maxWidth: column.width,
                            height: column.height,
                          }}
                          className={`${styles.cell} ${
                            isSelected
                              ? column.isEditable !== false
                                ? styles['selected-cell']
                                : styles['selected-cell-non-editable']
                              : ''
                          } ${isCellEditing ? styles['editing-cell'] : ''} ${
                            column.isEditable !== false
                              ? styles['editable-cell']
                              : ''
                          }`}
                          onClick={(event) => {
                            if (isCellEditing) {
                              event.stopPropagation()
                              return
                            }
                            handleCellClick(rowIndex, colIndex, event)
                          }}
                          onDoubleClick={(event) => {
                            if (isCellEditing) {
                              event.stopPropagation()
                              return
                            }
                            handleCellDoubleClick(rowIndex, colIndex, event)
                          }}
                          onMouseDown={(event) => {
                            if (isCellEditing) {
                              event.stopPropagation()
                            }
                          }}
                        >
                          {renderCellContent(
                            record,
                            column,
                            rowIndex,
                            colIndex,
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))
              : Array.from({ length: limit }).map((_, index) => (
                  <tr key={`skeleton-row-${index}`}>
                    {columns.map((column, colIndex) => (
                      <td
                        key={`skeleton-cell-${column.key}-${index}-${colIndex}`}
                        className={styles.cell}
                        style={{
                          width: column.width,
                          minWidth: column.width,
                          maxWidth: column.width,
                        }}
                      >
                        <Skeleton height="1.26rem" width="100%" />
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
